/**
 * F5 phone-side rules — PURE functions over the projected store state, unit-tested. The phone
 * refuses what the spec says the phone refuses (with a beep and a reason code); the server checks
 * everything again on sync and never refuses for a rule (it applies, holds the lot, raises an
 * exception).
 */
import { divRoundHalfUp } from '@/lib/money'
import { istDaysBetween } from '@/lib/ist'
import type { Grams } from '@/lib/weight'
import type { StoreLocation, StoreSettings, Trip } from '@/offline/storeCache'
import type { ScanRefusal } from './commands'
import {
  bayOf,
  declarationInfo,
  siteOf,
  type BagView,
  type SampleView,
  type StoreState,
} from './projection'

// ---------------------------------------------------------------------------------------------
// What is this code?
// ---------------------------------------------------------------------------------------------

export type ScanKind =
  | { kind: 'BAG'; bag: BagView }
  | { kind: 'SEAL'; sample: SampleView }
  | { kind: 'LOCATION'; location: StoreLocation }
  | { kind: 'DEAD'; status: 'VOID' | 'RETIRED' }
  | { kind: 'UNKNOWN' }

const norm = (c: string) => c.trim().toUpperCase()

/** Look a scanned (or typed) code up in the phone's cache. What it shows comes from data, not the code. */
export function classifyScan(state: StoreState, code: string): ScanKind {
  const c = norm(code)
  const dead = state.deadTags.get(c)
  if (dead) return { kind: 'DEAD', status: dead }
  const bag = state.bagByPayload.get(c)
  if (bag) {
    if (bag.status === 'VOID') return { kind: 'DEAD', status: 'VOID' }
    return { kind: 'BAG', bag }
  }
  const sample = state.sampleByPayload.get(c)
  if (sample) return { kind: 'SEAL', sample }
  const location = state.locationByCode.get(c)
  if (location) return { kind: 'LOCATION', location }
  return { kind: 'UNKNOWN' }
}

/** A refused scan that the server must hear about (TAG_VOID_SEEN / TAG_UNKNOWN). */
export function isReportedRefusal(r: ScanRefusal): boolean {
  return r === 'TAG_VOID' || r === 'TAG_RETIRED' || r === 'TAG_UNKNOWN'
}

function deadRefusal(k: ScanKind): ScanRefusal | null {
  if (k.kind === 'DEAD') return k.status === 'VOID' ? 'TAG_VOID' : 'TAG_RETIRED'
  if (k.kind === 'UNKNOWN') return 'TAG_UNKNOWN'
  return null
}

export type ScanVerdict<T> =
  | { ok: true; value: T }
  | { ok: false; reason: ScanRefusal }
  /** Already on this list — ignored, the count does not change. */
  | { ok: false; reason: 'DUPLICATE' }

// ---------------------------------------------------------------------------------------------
// F5.1 Load
// ---------------------------------------------------------------------------------------------

export interface LoadContext {
  fromLocationId: string
  vehicleClean: boolean
  /** container ids already on this loading list */
  onList: ReadonlySet<string>
  /** sample ids already on this loading list */
  samplesOnList: ReadonlySet<string>
}

/** Is `locationId` at or below `ancestorId`? */
function within(state: StoreState, locationId: string | null | undefined, ancestorId: string) {
  let cur = locationId ? state.locations.get(locationId) : undefined
  if (locationId === ancestorId) return true
  const seen = new Set<string>()
  while (cur && !seen.has(cur.id)) {
    if (cur.id === ancestorId) return true
    seen.add(cur.id)
    cur = cur.parent_id ? state.locations.get(cur.parent_id) : undefined
  }
  return false
}

/** Bag field at loading (F5.1 validations). */
export function checkLoadBag(
  state: StoreState,
  code: string,
  ctx: LoadContext,
): ScanVerdict<BagView> {
  const k = classifyScan(state, code)
  const dead = deadRefusal(k)
  if (dead) return { ok: false, reason: dead }
  if (k.kind === 'SEAL' || k.kind === 'LOCATION') return { ok: false, reason: 'TAG_WRONG_KIND' }
  if (k.kind !== 'BAG') return { ok: false, reason: 'TAG_UNKNOWN' }
  const bag = k.bag
  if (ctx.onList.has(bag.container_id)) return { ok: false, reason: 'DUPLICATE' }
  if (bag.shipment_id) return { ok: false, reason: 'ALREADY_LOADED' }
  if (bag.status !== 'SEALED' && bag.status !== 'STORED') return { ok: false, reason: 'NOT_STORED' }
  if (!within(state, bag.location_id, ctx.fromLocationId))
    return { ok: false, reason: 'NOT_AT_ORIGIN' }
  // R30: with a dirty bed only ordinary (CONV) bags may go. The lot's hold does not matter.
  const lot = state.lots.get(bag.unit_id)
  const conv = bag.stream === 'CONV' || lot?.stream === 'CONV'
  if (!ctx.vehicleClean && !conv) return { ok: false, reason: 'VEHICLE_NOT_CLEAN' }
  return { ok: true, value: bag }
}

/** Sample field at loading: only SEAL codes. */
export function checkLoadSample(
  state: StoreState,
  code: string,
  ctx: LoadContext,
): ScanVerdict<SampleView> {
  const k = classifyScan(state, code)
  const dead = deadRefusal(k)
  if (dead) return { ok: false, reason: dead }
  if (k.kind !== 'SEAL') return { ok: false, reason: 'TAG_WRONG_KIND' }
  if (ctx.samplesOnList.has(k.sample.sample_id)) return { ok: false, reason: 'DUPLICATE' }
  if (k.sample.shipment_id) return { ok: false, reason: 'ALREADY_LOADED' }
  if (!within(state, k.sample.location_id, ctx.fromLocationId))
    return { ok: false, reason: 'NOT_AT_ORIGIN' }
  return { ok: true, value: k.sample }
}

export interface LotLoadCount {
  unit_id: string
  on: number
  /** Bags of the lot still at the origin (not on this list). */
  left: number
  total: number
}

/** Per-lot counters ("5 of 9 on") and which lots are part-loaded. */
export function lotLoadCounts(
  state: StoreState,
  fromLocationId: string,
  onList: readonly BagView[],
): LotLoadCount[] {
  const byLot = new Map<string, LotLoadCount>()
  for (const b of onList) {
    const c = byLot.get(b.unit_id) ?? {
      unit_id: b.unit_id,
      on: 0,
      left: 0,
      total: state.lots.get(b.unit_id)?.bag_count ?? 0,
    }
    c.on++
    byLot.set(b.unit_id, c)
  }
  const listed = new Set(onList.map((b) => b.container_id))
  for (const b of state.bags.values()) {
    const c = byLot.get(b.unit_id)
    if (!c || listed.has(b.container_id) || b.shipment_id) continue
    if (
      (b.status === 'SEALED' || b.status === 'STORED') &&
      within(state, b.location_id, fromLocationId)
    )
      c.left++
  }
  return [...byLot.values()]
}

export interface TripDetails {
  to_location_id: string | null
  vehicle_no: string
  driver_name: string
  driver_phone: string
  challan_no: string
  vehicle_clean: boolean | null
}

export type DispatchBlock =
  | 'DESTINATION'
  | 'SAME_PLACE'
  | 'VEHICLE_NO'
  | 'DRIVER_NAME'
  | 'DRIVER_PHONE'
  | 'CHALLAN_NO'
  | 'VEHICLE_CLEAN'
  | 'NO_BAGS'
  | 'HAND_COUNT'
  | 'PHOTO'
  | 'PART_LOAD'

/** Why Dispatch is still locked (empty = ready). F5.1 phone validations. */
export function dispatchBlocks(args: {
  fromLocationId: string
  details: TripDetails
  bagCount: number
  handCount: number | null
  photoId: string | null
  partialLots: readonly string[]
  confirmedPartial: ReadonlySet<string>
}): DispatchBlock[] {
  const d = args.details
  const out: DispatchBlock[] = []
  if (!d.to_location_id) out.push('DESTINATION')
  else if (d.to_location_id === args.fromLocationId) out.push('SAME_PLACE')
  if (!d.vehicle_no.trim()) out.push('VEHICLE_NO')
  if (!d.driver_name.trim()) out.push('DRIVER_NAME')
  if (!/^\d{10}$/.test(d.driver_phone)) out.push('DRIVER_PHONE')
  if (!d.challan_no.trim()) out.push('CHALLAN_NO')
  if (d.vehicle_clean === null) out.push('VEHICLE_CLEAN')
  if (args.bagCount === 0) out.push('NO_BAGS')
  if (args.handCount !== args.bagCount) out.push('HAND_COUNT')
  if (!args.photoId) out.push('PHOTO')
  if (args.partialLots.some((u) => !args.confirmedPartial.has(u))) out.push('PART_LOAD')
  return out
}

// ---------------------------------------------------------------------------------------------
// F5.2 Receive — manifest reconciliation
// ---------------------------------------------------------------------------------------------

export interface ReceiveScan {
  qr_payload: string
  scanned_at: string
}

export interface Reconciliation {
  /** Manifest items scanned off. */
  unloaded: Array<{ item: Trip['items'][number]; scanned_at: string }>
  /** Good tags not on the manifest (received; the server decides LOW/HIGH). */
  extras: Array<{ bag: BagView; scanned_at: string }>
  /** Manifest items not scanned (and not yet marked missing). */
  open: Trip['items']
  /** Items marked missing. */
  missing: Trip['items']
  /** Scans of codes that cannot be received (void / unknown / wrong kind). */
  refused: Array<{ qr_payload: string; reason: ScanRefusal; scanned_at: string }>
  /** Per lot: on the manifest, scanned off. */
  byLot: Map<string, { expected: number; scanned: number }>
  bagsExpected: number
  bagsScanned: number
}

/**
 * Scanned vs expected → unloaded / extra / missing / refused (F5.2). Duplicate scans count once.
 * `markedMissing` holds item ids the keeper marked missing after Vehicle empty.
 */
export function reconcileManifest(
  state: StoreState,
  trip: Pick<Trip, 'items'>,
  scans: readonly ReceiveScan[],
  markedMissing: ReadonlySet<string> = new Set(),
): Reconciliation {
  const byPayload = new Map(trip.items.map((i) => [norm(i.qr_payload), i]))
  const seen = new Set<string>()
  const unloaded: Reconciliation['unloaded'] = []
  const extras: Reconciliation['extras'] = []
  const refused: Reconciliation['refused'] = []
  for (const s of scans) {
    const c = norm(s.qr_payload)
    if (seen.has(c)) continue
    seen.add(c)
    const item = byPayload.get(c)
    if (item) {
      unloaded.push({ item, scanned_at: s.scanned_at })
      continue
    }
    const k = classifyScan(state, c)
    const dead = deadRefusal(k)
    if (dead) refused.push({ qr_payload: c, reason: dead, scanned_at: s.scanned_at })
    else if (k.kind === 'BAG') extras.push({ bag: k.bag, scanned_at: s.scanned_at })
    else refused.push({ qr_payload: c, reason: 'TAG_WRONG_KIND', scanned_at: s.scanned_at })
  }
  const unloadedIds = new Set(unloaded.map((u) => u.item.item_id))
  const notScanned = trip.items.filter((i) => !unloadedIds.has(i.item_id))
  const missing = notScanned.filter((i) => markedMissing.has(i.item_id))
  const open = notScanned.filter((i) => !markedMissing.has(i.item_id))

  const byLot = new Map<string, { expected: number; scanned: number }>()
  for (const i of trip.items) {
    if (!i.container_id) continue
    const key = i.unit_id ?? '—'
    const v = byLot.get(key) ?? { expected: 0, scanned: 0 }
    v.expected++
    if (unloadedIds.has(i.item_id)) v.scanned++
    byLot.set(key, v)
  }
  const bagItems = trip.items.filter((i) => i.container_id)
  return {
    unloaded,
    extras,
    open,
    missing,
    refused,
    byLot,
    bagsExpected: bagItems.length,
    bagsScanned: unloaded.filter((u) => u.item.container_id).length + extras.length,
  }
}

// ---------------------------------------------------------------------------------------------
// F5.2 Check weights — integer grams only
// ---------------------------------------------------------------------------------------------

export interface CheckWeighResult {
  /** value − tare */
  receiveNet: Grams
  /** Gate net less the expected drying for the crop and days since commit. */
  expectedNet: Grams
  drying: Grams
  /** receiveNet − expectedNet (signed). */
  diff: Grams
  tolerance: Grams
  within: boolean
}

/** bp of `g`, rounded half-up once. */
function bpOf(g: Grams, bp: number): Grams {
  return Number(divRoundHalfUp(BigInt(g) * BigInt(bp), 10_000n))
}

/**
 * Receive tolerance after expected drying (F5.2): drying = gate net × min(bp/day × days, max bp);
 * tolerance = max(fixed grams, gate net × tolerance bp). Everything in whole grams.
 */
export function checkWeigh(args: {
  valueG: Grams
  tareG: Grams
  netGateG: Grams
  cropId: string | null | undefined
  committedAt: string | null | undefined
  takenAt: string | number | Date
  settings: Pick<
    StoreSettings,
    'drying_bp_per_day' | 'drying_max_bp' | 'receive_tolerance_g' | 'receive_tolerance_bp'
  >
}): CheckWeighResult {
  const { valueG, tareG, netGateG, settings } = args
  const days = args.committedAt ? Math.max(0, istDaysBetween(args.committedAt, args.takenAt)) : 0
  const perDay =
    (args.cropId ? settings.drying_bp_per_day[args.cropId] : undefined) ??
    settings.drying_bp_per_day['*'] ??
    0
  const dryingBp = Math.min(perDay * days, settings.drying_max_bp)
  const drying = bpOf(netGateG, dryingBp)
  const expectedNet = netGateG - drying
  const receiveNet = valueG - tareG
  const diff = receiveNet - expectedNet
  const tolerance = Math.max(
    settings.receive_tolerance_g,
    bpOf(netGateG, settings.receive_tolerance_bp),
  )
  return { receiveNet, expectedNet, drying, diff, tolerance, within: Math.abs(diff) <= tolerance }
}

/**
 * Which bags need a check weight: the server's picks (recheck_required), every bag of a PAPER lot,
 * every bag of a lot with a confirmed out-of-tolerance weight, extras (missed at loading); when the
 * server picked none for a lot, the phone picks `perLot` at random (picked_by PHONE).
 */
export function checkWeighPicks(args: {
  state: StoreState
  trip: Pick<Trip, 'items'>
  extras: readonly BagView[]
  perLot: number
  /** Lots where a weight was confirmed out of tolerance → every bag. */
  allBagsLots: ReadonlySet<string>
  random?: () => number
}): Map<string, 'SERVER' | 'PHONE'> {
  const { state, trip, perLot } = args
  const random = args.random ?? Math.random
  const picks = new Map<string, 'SERVER' | 'PHONE'>()
  const byLot = new Map<string, string[]>()
  for (const i of trip.items) {
    if (!i.container_id) continue
    const unit = i.unit_id ?? state.bags.get(i.container_id)?.unit_id ?? ''
    const list = byLot.get(unit) ?? []
    list.push(i.container_id)
    byLot.set(unit, list)
    if (i.recheck_required) picks.set(i.container_id, 'SERVER')
  }
  for (const [unit, ids] of byLot) {
    const lot = state.lots.get(unit)
    if (lot?.capture_mode === 'PAPER' || args.allBagsLots.has(unit)) {
      for (const id of ids) if (!picks.has(id)) picks.set(id, 'PHONE')
      continue
    }
    if (ids.some((id) => picks.get(id) === 'SERVER')) continue
    const pool = [...ids]
    for (let n = 0; n < Math.min(perLot, ids.length); n++) {
      const idx = Math.floor(random() * pool.length)
      const [id] = pool.splice(idx, 1)
      if (id) picks.set(id, 'PHONE')
    }
  }
  for (const b of args.extras) if (!picks.has(b.container_id)) picks.set(b.container_id, 'PHONE')
  for (const unit of args.allBagsLots)
    for (const b of args.extras) if (b.unit_id === unit) picks.set(b.container_id, 'PHONE')
  return picks
}

/** R11: the gate weigher may not type the check weight. */
export function isSameWeigher(bag: Pick<BagView, 'gate_weigher_id'>, actorId: string): boolean {
  return Boolean(bag.gate_weigher_id) && bag.gate_weigher_id === actorId
}

export type ReceiveBlock = 'NO_TRIP' | 'OPEN_ITEMS' | 'CHECK_WEIGHTS' | 'NOT_EMPTY'

export function receiveBlocks(args: {
  hasManifest: boolean
  vehicleEmpty: boolean
  openItems: number
  weightsMissing: number
}): ReceiveBlock[] {
  const out: ReceiveBlock[] = []
  if (!args.hasManifest) out.push('NO_TRIP')
  if (!args.vehicleEmpty) out.push('NOT_EMPTY')
  if (args.openItems > 0) out.push('OPEN_ITEMS')
  if (args.weightsMissing > 0) out.push('CHECK_WEIGHTS')
  return out
}

// ---------------------------------------------------------------------------------------------
// F5.3 / F5.9 Put away and move
// ---------------------------------------------------------------------------------------------

/** The place label itself (F5.3: BAY or POSITION, active, this site). */
export function checkPlace(state: StoreState, location: StoreLocation): ScanRefusal | null {
  if (!location.active) return 'LOCATION_INACTIVE'
  if (location.level !== 'BAY' && location.level !== 'POSITION') return 'LEVEL_NOT_ALLOWED'
  const site = siteOf(state, location.id)
  if (site && site.id !== state.siteId) return 'OTHER_SITE'
  return null
}

export interface PlaceContext {
  mode: 'PUT_AWAY' | 'MOVE'
  location: StoreLocation
  now: number
  /** container ids already on this list (and their lots) */
  onList: readonly BagView[]
}

/** A bag scanned at a place — site, stored, declaration, stream and one-lot-per-spot rules. */
export function checkPlaceBag(
  state: StoreState,
  bag: BagView,
  ctx: PlaceContext,
): ScanRefusal | 'DUPLICATE' | null {
  if (ctx.onList.some((b) => b.container_id === bag.container_id)) return 'DUPLICATE'
  if (bag.status !== 'STORED' || bag.shipment_id) return 'NOT_STORED'
  const bagSite = siteOf(state, bag.location_id)
  const placeSite = siteOf(state, ctx.location.id)
  if (bagSite && placeSite && bagSite.id !== placeSite.id) return 'OTHER_SITE'
  if (ctx.mode === 'MOVE' && bag.location_id === ctx.location.id) return 'SAME_PLACE'
  const lot = state.lots.get(bag.unit_id)
  const conv = bag.stream === 'CONV' || lot?.stream === 'CONV' || lot?.unit_type === 'ORPHAN'
  const decl = declarationInfo(state, ctx.location.id, ctx.now).status
  const bay = bayOf(state, ctx.location.id)
  const holdArea = ctx.location.role === 'HOLD_AREA' || bay?.role === 'HOLD_AREA'
  if (conv) {
    if (decl === 'CLEAN') return 'CONV_IN_CLEAN_BAY'
  } else if (decl !== 'CLEAN' && !holdArea) {
    return 'NOT_DECLARED'
  }
  if (ctx.location.level === 'POSITION') {
    const others = new Set<string>()
    for (const b of state.bags.values())
      if (b.location_id === ctx.location.id && !b.shipment_id && b.status === 'STORED')
        others.add(b.unit_id)
    for (const b of ctx.onList) others.add(b.unit_id)
    others.delete(bag.unit_id)
    if (others.size > 0) return 'POSITION_HAS_OTHER_LOT'
  }
  return null
}

/** A seal at a place: only into the sample store (F5.3). */
export function checkPlaceSample(
  state: StoreState,
  sample: SampleView,
  location: StoreLocation,
  onList: readonly SampleView[],
): ScanRefusal | 'DUPLICATE' | null {
  if (onList.some((s) => s.sample_id === sample.sample_id)) return 'DUPLICATE'
  const bay = bayOf(state, location.id)
  if (location.role !== 'SAMPLE_STORE' && bay?.role !== 'SAMPLE_STORE') return 'NOT_SAMPLE_STORE'
  if (sample.shipment_id) return 'NOT_STORED'
  return null
}

/** Bags of `unitId` at `locationId` after this list is applied — the "9 of 9 here" counter. */
export function lotCountHere(
  state: StoreState,
  unitId: string,
  locationId: string,
  onList: readonly BagView[],
): { here: number; total: number } {
  const ids = new Set<string>()
  for (const b of state.bags.values())
    if (
      b.unit_id === unitId &&
      b.location_id === locationId &&
      b.status === 'STORED' &&
      !b.shipment_id
    )
      ids.add(b.container_id)
  for (const b of onList) if (b.unit_id === unitId) ids.add(b.container_id)
  return { here: ids.size, total: state.lots.get(unitId)?.bag_count ?? ids.size }
}

// ---------------------------------------------------------------------------------------------
// F5.4 Declaration
// ---------------------------------------------------------------------------------------------

export function checkDeclarationPlace(location: StoreLocation): ScanRefusal | null {
  if (!location.active) return 'LOCATION_INACTIVE'
  return location.level === 'BAY' ? null : 'NOT_A_BAY'
}

/** Sign is enabled when every question is answered (distance when a stack exists) and a photo is taken. */
export function declarationMissing(
  form: StoreState['form'],
  answers: Record<string, boolean | number | null | undefined>,
): string[] {
  return form.questions
    .filter((q) => {
      const v = answers[q.key]
      if (q.required_if) return answers[q.required_if] === true && (v === null || v === undefined)
      return v === null || v === undefined
    })
    .map((q) => q.key)
}

// ---------------------------------------------------------------------------------------------
// F5.6 Count a place · F5-B1.a candidate match
// ---------------------------------------------------------------------------------------------

export interface CountComparison {
  expected: BagView[]
  seen: BagView[]
  unseen: BagView[]
  /** Good tags seen here that the phone did not expect here. */
  extra: BagView[]
  /** Codes seen that are not live bags (void / unknown / seals / labels). */
  unknown: string[]
  /** Per lot at this place: expected, seen, short. */
  byLot: Array<{ unit_id: string; expected: number; seen: number; short: number }>
  matches: boolean
}

/** Compare the tags seen with the bags the phone expects at the place and below (F5.6). */
export function compareCount(
  state: StoreState,
  expected: readonly BagView[],
  seenCodes: readonly string[],
): CountComparison {
  const expectedIds = new Set(expected.map((b) => b.container_id))
  const seenSet = new Set<string>()
  const seen: BagView[] = []
  const extra: BagView[] = []
  const unknown: string[] = []
  for (const raw of seenCodes) {
    const c = norm(raw)
    if (seenSet.has(c)) continue
    seenSet.add(c)
    const k = classifyScan(state, c)
    if (k.kind !== 'BAG') {
      unknown.push(c)
      continue
    }
    if (expectedIds.has(k.bag.container_id)) seen.push(k.bag)
    else extra.push(k.bag)
  }
  const seenIds = new Set(seen.map((b) => b.container_id))
  const unseen = expected.filter((b) => !seenIds.has(b.container_id))
  const lots = new Map<string, { unit_id: string; expected: number; seen: number; short: number }>()
  for (const b of expected) {
    const v = lots.get(b.unit_id) ?? { unit_id: b.unit_id, expected: 0, seen: 0, short: 0 }
    v.expected++
    if (seenIds.has(b.container_id)) v.seen++
    else v.short++
    lots.set(b.unit_id, v)
  }
  return {
    expected: [...expected],
    seen,
    unseen,
    extra,
    unknown,
    byLot: [...lots.values()],
    matches: unseen.length === 0 && extra.length === 0 && unknown.length === 0,
  }
}

/** Last known net of a bag: current, else gate. */
export function lastNet(b: Pick<BagView, 'net_g_current' | 'net_g_gate'>): Grams | null {
  return b.net_g_current ?? b.net_g_gate ?? null
}

/**
 * Lost-tag evidence preview (F5-B1.a): exactly one unseen bag whose last net is within the match
 * tolerance of the new sack's net → that bag; otherwise null (the sack becomes an ordinary orphan).
 * The server decides again on sync.
 */
export function candidateMatch(
  unseen: readonly BagView[],
  newNetG: Grams,
  toleranceG: Grams,
): { candidate: BagView | null; matches: BagView[] } {
  const matches = unseen.filter((b) => {
    const n = lastNet(b)
    return n !== null && Math.abs(n - newNetG) <= toleranceG
  })
  return { candidate: matches.length === 1 ? (matches[0] ?? null) : null, matches }
}

/** New replacement tag: from the keeper's replacement roll, never used (F5-B1.a). */
export function checkReplacementTag(
  state: StoreState,
  code: string,
  actorId: string,
): ScanRefusal | null {
  const c = norm(code)
  const k = classifyScan(state, c)
  if (k.kind === 'DEAD') return k.status === 'VOID' ? 'TAG_VOID' : 'TAG_RETIRED'
  if (k.kind === 'BAG' || state.usedTags.has(c)) return 'TAG_ALREADY_USED'
  if (k.kind !== 'UNKNOWN') return 'TAG_WRONG_KIND'
  const roll = state.cache.rolls.find((r) => {
    if (r.kind !== 'BAG') return false
    if (r.issued_to_id && r.issued_to_id !== actorId) return false
    const prefix = r.payload_prefix.toUpperCase()
    if (!c.startsWith(prefix)) return false
    const n = Number(c.slice(prefix.length).replace(/^[A-Z]*/, ''))
    return Number.isInteger(n) && n >= r.start_sequence && n <= r.end_sequence
  })
  if (!roll) return 'NOT_YOUR_ROLL'
  if (roll.used.some((u) => u.toUpperCase() === c)) return 'TAG_ALREADY_USED'
  return null
}

/** Old tag, if scanned: a live bag with its place here (F5-B1.a). */
export function checkOldTag(
  state: StoreState,
  code: string,
  expectedHere: readonly BagView[],
): ScanVerdict<BagView> {
  const k = classifyScan(state, code)
  const dead = deadRefusal(k)
  if (dead) return { ok: false, reason: dead }
  if (k.kind !== 'BAG') return { ok: false, reason: 'TAG_WRONG_KIND' }
  if (!expectedHere.some((b) => b.container_id === k.bag.container_id))
    return { ok: false, reason: 'OLD_TAG_NOT_HERE' }
  return { ok: true, value: k.bag }
}

// ---------------------------------------------------------------------------------------------
// F5.6 Red ties
// ---------------------------------------------------------------------------------------------

/** Bags of ordinary lots still without a red tie, here at this site. */
export function redTieList(state: StoreState): BagView[] {
  return [...state.bags.values()].filter((b) => {
    if (b.status !== 'STORED' || b.shipment_id) return false
    const lot = state.lots.get(b.unit_id)
    return b.red_tie_pending || (lot?.stream === 'CONV' && b.stream === 'MAIN')
  })
}
